/**
 * A heat map drawn on a canvas over the map (Google retired its own heat map layer). Each point adds a
 * soft blot; where many overlap the color runs from blue through yellow to red.
 */
export interface HeatOverlay {
  setPoints(points: { lat: number; lon: number }[]): void;
  setMap(map: google.maps.Map | null): void;
}

const RADIUS = 22;

/** Blue → green → yellow → red by how much overlaps (0..255). */
function palette(): Uint8ClampedArray {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 1;
  const g = c.getContext("2d")!;
  const grad = g.createLinearGradient(0, 0, 256, 0);
  grad.addColorStop(0.15, "#2563EB");
  grad.addColorStop(0.4, "#10B981");
  grad.addColorStop(0.65, "#FACC15");
  grad.addColorStop(1, "#DC2626");
  g.fillStyle = grad;
  g.fillRect(0, 0, 256, 1);
  return g.getImageData(0, 0, 256, 1).data;
}

export function createHeatOverlay(): HeatOverlay {
  class Heat extends google.maps.OverlayView {
    private canvas = document.createElement("canvas");
    private points: { lat: number; lon: number }[] = [];
    private colors = palette();

    setPoints(points: { lat: number; lon: number }[]) {
      this.points = points;
      this.draw();
    }
    onAdd() {
      this.canvas.style.position = "absolute";
      this.canvas.style.pointerEvents = "none";
      this.getPanes()?.overlayLayer.appendChild(this.canvas);
    }
    onRemove() {
      this.canvas.remove();
    }
    draw() {
      const projection = this.getProjection();
      const map = this.getMap() as google.maps.Map | null;
      const bounds = map?.getBounds();
      if (!projection || !map || !bounds) return;
      const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest())!;
      const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast())!;
      const left = Math.min(sw.x, ne.x) - RADIUS;
      const top = Math.min(sw.y, ne.y) - RADIUS;
      const width = Math.max(1, Math.round(Math.abs(ne.x - sw.x) + RADIUS * 2));
      const height = Math.max(1, Math.round(Math.abs(sw.y - ne.y) + RADIUS * 2));
      Object.assign(this.canvas.style, { left: `${left}px`, top: `${top}px`, width: `${width}px`, height: `${height}px` });
      this.canvas.width = width;
      this.canvas.height = height;
      const g = this.canvas.getContext("2d")!;
      g.clearRect(0, 0, width, height);
      // More points: each blot a little fainter, so a busy map doesn't turn solid red.
      const alpha = Math.max(0.08, Math.min(0.35, 30 / Math.sqrt(this.points.length + 1) / 10));
      for (const p of this.points) {
        const at = projection.fromLatLngToDivPixel(new google.maps.LatLng(p.lat, p.lon));
        if (!at) continue;
        const x = at.x - left;
        const y = at.y - top;
        if (x < -RADIUS || y < -RADIUS || x > width + RADIUS || y > height + RADIUS) continue;
        const grad = g.createRadialGradient(x, y, 0, x, y, RADIUS);
        grad.addColorStop(0, `rgba(0,0,0,${alpha})`);
        grad.addColorStop(1, "rgba(0,0,0,0)");
        g.fillStyle = grad;
        g.fillRect(x - RADIUS, y - RADIUS, RADIUS * 2, RADIUS * 2);
      }
      const img = g.getImageData(0, 0, width, height);
      const d = img.data;
      for (let i = 0; i < d.length; i += 4) {
        const a = d[i + 3]!;
        if (!a) continue;
        const c = Math.min(255, a * 2) * 4;
        d[i] = this.colors[c]!;
        d[i + 1] = this.colors[c + 1]!;
        d[i + 2] = this.colors[c + 2]!;
        d[i + 3] = Math.min(200, 60 + a * 1.5);
      }
      g.putImageData(img, 0, 0);
    }
  }
  return new Heat();
}
